import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../Icon";
import Magnetic from "../Magnetic";
import Reveal from "../Reveal";
import SectionIntro from "./SectionIntro";
import SplitText from "./SplitText";
import { CTA } from "../../data/about";

/**
 * The close.
 *
 * Written as the last line of the story rather than as a contact card: the
 * same display type the page opened on, one sentence, one clear action. The
 * button pursues the pointer a few pixels — the small amount of pursuit is
 * what makes the last thing on the page feel answerable.
 */

function AboutCta() {
  const navigate = useNavigate();

  return (
    <section
      id="contact-cta"
      className="relative isolate overflow-hidden bg-[#0B0518] py-24 lg:py-32"
    >
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_88%_74%_at_50%_112%,#6A0DAD_0%,#2E1065_44%,#0B0518_100%)]" />
        <div
          className="mw-aurora absolute -bottom-52 left-1/2 h-[46rem] w-[46rem] -translate-x-1/2 rounded-full bg-[#A855F7]/25 blur-[150px]"
          style={{ animationDelay: "-7s" }}
        />
        <div
          className="mw-beam absolute -top-1/4 right-[16%] h-[150%] w-[16rem]"
          style={{ "--mw-beam-rot": "-12deg" }}
        />
        <div className="mw-sheen absolute inset-0" />
        <div className="mw-grain absolute inset-0 opacity-[0.13] mix-blend-overlay" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <Reveal>
          <SectionIntro index={CTA.index} label={CTA.label} tone="dark" />
        </Reveal>

        <SplitText
          as="h2"
          lines={CTA.lines}
          stagger={70}
          className="mw-display mt-8 text-white"
        />

        <div className="mt-12 flex flex-col gap-10 lg:mt-16 lg:flex-row lg:items-end lg:justify-between">
          <Reveal delay={140}>
            <p className="mw-lede max-w-md text-[#DCC9F2]">{CTA.body}</p>
          </Reveal>

          <Reveal delay={220}>
            <div className="flex flex-wrap items-center gap-4">
              <Magnetic strength={0.28}>
                <button
                  type="button"
                  onClick={() => navigate(CTA.action.to)}
                  className="group flex items-center gap-5 rounded-full bg-white py-2 pl-8 pr-2 text-[15px] font-bold text-[#1E1B16] transition-shadow duration-500 hover:shadow-[0_30px_60px_-25px_rgba(255,255,255,0.5)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FBCFE8]/60"
                >
                  {CTA.action.label}
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#6A0DAD] text-white transition-transform duration-500 [transition-timing-function:var(--mw-spring)] group-hover:rotate-45">
                    <Icon name="arrow" className="h-[18px] w-[18px]" />
                  </span>
                </button>
              </Magnetic>

              <button
                type="button"
                onClick={() => navigate(CTA.secondary.to)}
                className="rounded-full border border-white/25 px-7 py-3.5 text-[15px] font-semibold text-white/90 transition-colors duration-300 hover:border-white/50 hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-white/25"
              >
                {CTA.secondary.label}
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default AboutCta;
